import {
  type CommentMention,
  filterMentionCandidates,
  type MentionCandidate,
  mentionsStillInBody,
  mentionToken,
  readOpenMention,
} from '@bangle.io/supabase';

/**
 * Imperative @-mention composer for plain text inputs used by comment widgets.
 */
export function attachMentionInput(
  input: HTMLInputElement | HTMLTextAreaElement,
  getCandidates: () => MentionCandidate[],
): {
  getMentions: () => CommentMention[];
  destroy: () => void;
} {
  let picked: CommentMention[] = [];
  let selected = 0;
  let open: { from: number; to: number; query: string } | null = null;
  let options: MentionCandidate[] = [];
  let menu: HTMLDivElement | null = null;

  const closeMenu = () => {
    open = null;
    options = [];
    selected = 0;
    menu?.remove();
    menu = null;
  };

  const placeMenu = () => {
    if (!menu) return;
    const rect = input.getBoundingClientRect();
    menu.style.left = `${Math.min(rect.left, window.innerWidth - 240)}px`;
    menu.style.top = `${rect.bottom + 4}px`;
  };

  const renderMenu = () => {
    if (!open || options.length === 0) {
      closeMenu();
      return;
    }
    if (!menu) {
      menu = document.createElement('div');
      menu.className = 'sn-mention-suggest';
      menu.setAttribute('role', 'listbox');
      document.body.appendChild(menu);
    }
    menu.replaceChildren();
    options.forEach((opt, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'option');
      btn.className = `sn-mention-suggest-item${i === selected ? ' is-active' : ''}`;
      const label = document.createElement('span');
      label.className = 'sn-mention-suggest-label';
      label.textContent = opt.label;
      btn.appendChild(label);
      if (opt.display_name && opt.email && opt.label !== opt.email) {
        const secondary = document.createElement('span');
        secondary.className = 'sn-mention-suggest-secondary';
        secondary.textContent = opt.email;
        btn.appendChild(secondary);
      }
      btn.addEventListener('mousedown', (event) => {
        event.preventDefault();
        applyMention(opt);
      });
      menu?.appendChild(btn);
    });
    placeMenu();
  };

  const refresh = () => {
    const caret = input.selectionStart ?? input.value.length;
    const next = readOpenMention(input.value, caret);
    if (!next) {
      closeMenu();
      return;
    }
    open = next;
    options = filterMentionCandidates(getCandidates(), next.query);
    selected = 0;
    if (!options.length) {
      closeMenu();
      return;
    }
    renderMenu();
  };

  const applyMention = (opt: MentionCandidate) => {
    if (!open) return;
    const token = mentionToken(opt.label);
    const value = input.value;
    const next = `${value.slice(0, open.from)}${token} ${value.slice(open.to)}`;
    const caret = open.from + token.length + 1;
    input.value = next;
    input.setSelectionRange(caret, caret);
    if (!picked.some((p) => p.id === opt.id)) {
      picked = [...picked, { id: opt.id, label: opt.label }];
    }
    closeMenu();
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const onInput = () => refresh();
  const onClick = () => refresh();
  const onKeyDown = (event: KeyboardEvent) => {
    if (open && options.length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        selected = (selected + 1) % options.length;
        renderMenu();
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        selected = (selected - 1 + options.length) % options.length;
        renderMenu();
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        applyMention(options[selected]!);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
      }
    }
  };

  // Listeners go through a single element type: the
  // `HTMLInputElement | HTMLTextAreaElement` union only exposes the untyped
  // `addEventListener(string, ...)` signature, which rejects the
  // `KeyboardEvent` handler below.
  const field: HTMLElement = input;

  const onDocPointer = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (input.contains(target) || menu?.contains(target)) return;
    closeMenu();
  };

  field.addEventListener('input', onInput);
  field.addEventListener('click', onClick);
  field.addEventListener('keyup', onInput);
  field.addEventListener('keydown', onKeyDown);
  input.ownerDocument.addEventListener('pointerdown', onDocPointer, true);
  window.addEventListener('resize', placeMenu);

  return {
    getMentions: () => mentionsStillInBody(input.value, picked),
    destroy: () => {
      closeMenu();
      field.removeEventListener('input', onInput);
      field.removeEventListener('click', onClick);
      field.removeEventListener('keyup', onInput);
      field.removeEventListener('keydown', onKeyDown);
      input.ownerDocument.removeEventListener(
        'pointerdown',
        onDocPointer,
        true,
      );
      window.removeEventListener('resize', placeMenu);
    },
  };
}
